import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { navigableProps } from '../utils/navigation';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Chip,
  Typography,
  IconButton,
  Tooltip,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import { format } from 'date-fns';
import PageHeader from '../components/common/PageHeader';
import TableSkeleton from '../components/common/TableSkeleton';
import angeboteService from '../services/angeboteService';
import { useSearchParamsState } from '../hooks/useSearchParamsState';
import { usePermissions } from '../hooks/usePermissions';
import * as P from '../constants/permissions';
import { AngebotList } from '../types';

type SortField = 'angebotsnummer' | 'angebotsdatum' | 'titel' | 'kunde' | 'status' | 'nettobetrag';

const getStatusColor = (status: string): 'default' | 'primary' | 'success' | 'error' | 'warning' => {
  switch (status) {
    case 'Entwurf': return 'default';
    case 'Gesendet': return 'primary';
    case 'Gewonnen': return 'success';
    case 'Verloren': return 'error';
    case 'Zurueckgezogen': return 'warning';
    default: return 'default';
  }
};

const statusLabel = (status: string) => (status === 'Zurueckgezogen' ? 'Zurückgezogen' : status);

const AngeboteListPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [angebote, setAngebote] = useState<AngebotList[]>([]);
  const [loading, setLoading] = useState(true);

  const [params, setParams, resetParams, hasActiveFilters] = useSearchParamsState({
    defaults: {
      nr: '',
      titel: '',
      kunde: '',
      status: '',
      sort: '',
      abgeschlossen: false as boolean,
    },
  });

  const sortParts = (params.sort as string).split(':');
  const sortField: SortField | null = sortParts[0] ? (sortParts[0] as SortField) : null;
  const sortDirection: 'asc' | 'desc' = sortParts[1] === 'desc' ? 'desc' : 'asc';
  const showAbgeschlossen = params.abgeschlossen as boolean;

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setAngebote(await angeboteService.list());
      } catch (error) {
        console.error('Fehler beim Laden der Angebote:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const displayed = useMemo(() => {
    let rows = [...angebote];
    const nr = (params.nr as string).toLowerCase();
    const titel = (params.titel as string).toLowerCase();
    const kunde = (params.kunde as string).toLowerCase();
    const status = params.status as string;
    if (nr) rows = rows.filter((a) => a.angebotsnummer.toLowerCase().includes(nr));
    if (titel) rows = rows.filter((a) => a.titel.toLowerCase().includes(titel));
    if (kunde) rows = rows.filter((a) => (a.kunde_name || '').toLowerCase().includes(kunde));
    if (status) rows = rows.filter((a) => a.status === status);
    if (!showAbgeschlossen) {
      rows = rows.filter((a) => a.status !== 'Verloren' && a.status !== 'Zurueckgezogen');
    }
    if (sortField) {
      rows.sort((a, b) => {
        const dir = sortDirection === 'asc' ? 1 : -1;
        const val = (row: AngebotList) => {
          switch (sortField) {
            case 'kunde': return row.kunde_name || '';
            case 'nettobetrag': return row.nettobetrag;
            default: return (row as unknown as Record<string, string | number>)[sortField] ?? '';
          }
        };
        const av = val(a);
        const bv = val(b);
        if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
        return String(av).localeCompare(String(bv), 'de') * dir;
      });
    }
    return rows;
  }, [angebote, params, showAbgeschlossen, sortField, sortDirection]);

  const counts = useMemo(() => ({
    entwurf: angebote.filter((a) => a.status === 'Entwurf').length,
    gesendet: angebote.filter((a) => a.status === 'Gesendet').length,
    gewonnen: angebote.filter((a) => a.status === 'Gewonnen').length,
    verloren: angebote.filter((a) => a.status === 'Verloren').length,
  }), [angebote]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setParams({ sort: sortDirection === 'asc' ? `${field}:desc` : '' });
    } else {
      setParams({ sort: `${field}:asc` });
    }
  };

  const renderSortLabel = (field: SortField, label: string) => (
    <TableSortLabel
      active={sortField === field}
      direction={sortField === field ? sortDirection : 'asc'}
      onClick={() => handleSort(field)}
    >
      {label}
    </TableSortLabel>
  );

  return (
    <Box>
      <PageHeader
        title="Angebote"
        breadcrumbs={[{ label: 'Angebote' }]}
        onAdd={hasPermission(P.ANGEBOTE_CREATE) ? () => navigate('/angebote/neu') : undefined}
        addLabel="Neues Angebot"
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Entwurf', value: counts.entwurf },
          { label: 'Gesendet', value: counts.gesendet, color: 'primary.main' },
          { label: 'Gewonnen', value: counts.gewonnen, color: 'success.main' },
          { label: 'Verloren', value: counts.verloren, color: 'error.main' },
        ].map((card) => (
          <Grid item xs={6} sm={3} key={card.label}>
            <Card>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="body2" color="text.secondary">{card.label}</Typography>
                <Typography variant="h5" sx={{ color: card.color }}>{card.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 2, flexWrap: 'wrap' }}>
        <TextField size="small" label="Nummer" value={params.nr} onChange={(e) => setParams({ nr: e.target.value })} />
        <TextField size="small" label="Titel" value={params.titel} onChange={(e) => setParams({ titel: e.target.value })} />
        <TextField size="small" label="Kunde" value={params.kunde} onChange={(e) => setParams({ kunde: e.target.value })} />
        <TextField size="small" select label="Status" value={params.status} onChange={(e) => setParams({ status: e.target.value })} sx={{ minWidth: 160 }}>
          <MenuItem value="">Alle</MenuItem>
          {['Entwurf', 'Gesendet', 'Gewonnen', 'Verloren', 'Zurueckgezogen'].map((s) => (
            <MenuItem key={s} value={s}>{statusLabel(s)}</MenuItem>
          ))}
        </TextField>
        <FormControlLabel
          control={<Switch checked={showAbgeschlossen} onChange={(e) => setParams({ abgeschlossen: e.target.checked })} size="small" />}
          label="Verloren/Zurückgezogen anzeigen"
          slotProps={{ typography: { variant: 'body2', color: 'text.secondary' } }}
        />
        {hasActiveFilters && (
          <Button size="small" startIcon={<FilterListOffIcon />} onClick={resetParams}>Filter zurücksetzen</Button>
        )}
      </Box>

      {loading ? (
        <TableSkeleton columns={6} />
      ) : (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 600 }}>
            <TableHead>
              <TableRow>
                <TableCell>{renderSortLabel('angebotsnummer', 'Angebotsnr.')}</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{renderSortLabel('angebotsdatum', 'Datum')}</TableCell>
                <TableCell>{renderSortLabel('titel', 'Titel')}</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{renderSortLabel('kunde', 'Kunde')}</TableCell>
                <TableCell>{renderSortLabel('status', 'Status')}</TableCell>
                <TableCell align="right">{renderSortLabel('nettobetrag', 'Netto')}</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {displayed.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">Keine Angebote gefunden.</TableCell>
                </TableRow>
              ) : displayed.map((a) => (
                <TableRow key={a.id} hover sx={{ cursor: 'pointer' }} {...navigableProps(navigate, `/angebote/${a.id}`)}>
                  <TableCell>{a.angebotsnummer}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    {format(new Date(a.angebotsdatum), 'dd.MM.yyyy')}
                  </TableCell>
                  <TableCell>{a.titel}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{a.kunde_name || '–'}</TableCell>
                  <TableCell>
                    <Chip label={statusLabel(a.status)} size="small" color={getStatusColor(a.status)} />
                  </TableCell>
                  <TableCell align="right">{a.nettobetrag.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</TableCell>
                  <TableCell align="right">
                    <Tooltip title="PDF">
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); angeboteService.generatePdf(a.id).catch(() => undefined); }}>
                        <PictureAsPdfIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default AngeboteListPage;
